import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-oneportal";
import locationGuidanceImage from "@/assets/icons/locationex 3.png";
import { LocationDetailsStep } from "@/components/OnboardingTour/LocationDetailsStep";
import styles from "./LocationGuidanceModal.module.css";

export interface LocationGuidanceModalProps {
  locationId: string;
  locationName: string;
  showGuidanceSteps?: boolean;
  onClose: () => void;
  onContinue: () => void;
}

const GUIDANCE_STEP_COUNT = 3;

export const LocationGuidanceModal: React.FC<LocationGuidanceModalProps> = ({
  locationId,
  locationName,
  showGuidanceSteps = true,
  onClose,
  onContinue,
}) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [showLocationDetails, setShowLocationDetails] = useState(!showGuidanceSteps);
  const isLastStep = stepIndex === GUIDANCE_STEP_COUNT - 1;

  const handleNext = () => {
    if (isLastStep) {
      setShowLocationDetails(true);
      return;
    }
    setStepIndex((current) => current + 1);
  };

  const handleBackdropMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      className={styles.backdrop}
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
    >
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={
          showLocationDetails ? "location-details-title" : "location-guidance-title"
        }
        onMouseDown={(event) => event.stopPropagation()}
      >
        {showLocationDetails ? (
          <LocationDetailsStep
            locationId={locationId}
            embedded
            doneOnly
            onBack={() => setShowLocationDetails(false)}
            onFinish={onContinue}
          />
        ) : (
          <>
            <img
              className={styles.image}
              src={locationGuidanceImage}
              alt={t("locationGuidance.imageAlt", { location: locationName })}
            />
            <h2 id="location-guidance-title" className={styles.title}>
              {t(
                [
                  "locationGuidance.title",
                  "locationGuidance.navigationTitle",
                  "locationGuidance.permissionsTitle",
                ][stepIndex],
                { location: locationName },
              )}
            </h2>
            <p className={styles.description}>
              {t(
                [
                  "locationGuidance.description",
                  "locationGuidance.navigationDescription",
                  "locationGuidance.permissionsDescription",
                ][stepIndex],
                { location: locationName },
              )}
            </p>
            <div className={styles.dots} aria-hidden="true">
              {Array.from({ length: GUIDANCE_STEP_COUNT }, (_, index) => (
                <span
                  key={index}
                  className={`${styles.dot} ${stepIndex === index ? styles.activeDot : ""}`}
                />
              ))}
            </div>
            <div className={styles.actions}>
              <Button
                type="button"
                variant="highlightTertiary"
                onClick={onClose}
              >
                {t("locationGuidance.skip")}
              </Button>
              <Button
                type="button"
                variant="primary"
                className={styles.nextButton}
                onClick={handleNext}
              >
                {t("locationGuidance.next")}
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default LocationGuidanceModal;
